import { ArrowUp, Bot, Check, ChevronDown, ChevronRight, Command, FileCheck2, LockKeyhole, RotateCcw, ShieldCheck, Sparkles, UserRound, X } from "lucide-react";
import { FormEvent, KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { resolveSkillPrompt, visiblePortfolioSkills, type PortfolioSkill } from "../../shared/skills";
import type { ChatMessage, Citation, DepthMode, EvidenceMode } from "../../shared/types";
import { ApiError, createAiSession, sendChat } from "../api";
import { initialAssistantMessage, type AssistantHistory } from "../assistant-history";
import { ResponseActions } from "./ResponseActions";
import { ThinkingStatus } from "./ThinkingStatus";
import { TurnstileWidget } from "./TurnstileWidget";
import "./assistant-layout.css";

type Props = {
  siteKey: string;
  modelLabel: string;
  theme: "light" | "dark";
  componentAiEnabled: boolean;
  compact?: boolean;
  autoFocus?: boolean;
  onClose?: () => void;
  initialPrompt?: string;
  clearInitialPromptFromUrl?: boolean;
  history?: AssistantHistory;
  onHistoryChange?: (history: AssistantHistory) => void;
};
const SHOW_HOW_IT_WORKS = false;

function Selector<T extends string>({ label, value, options, optionLabels, onChange }: { label: string; value: T; options: T[]; optionLabels?: Partial<Record<T, string>>; onChange: (value: T) => void }) {
  return (
    <label className="select-control">
      <span>{label}</span>
      <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value as T)}>
        {options.map((option) => <option key={option} value={option}>{optionLabels?.[option] ?? option[0].toUpperCase() + option.slice(1)}</option>)}
      </select>
      <ChevronDown size={13} aria-hidden="true" />
    </label>
  );
}

function SkillsPanel({ onInsert }: { onInsert: (skill: PortfolioSkill) => void }) {
  const [expanded, setExpanded] = useState("");
  return (
    <aside className="skills-panel" aria-labelledby="skills-title">
      <div className="skills-heading">
        <div><span className="section-kicker">Prompt library</span><h2 id="skills-title">Skills</h2></div>
        <kbd>/</kbd>
      </div>
      <p className="skills-intro">Select a skill from the library or just start chatting.</p>
      <div className="skill-list">
        {visiblePortfolioSkills.map((skill) => {
          const isExpanded = expanded === skill.command;
          return <article className={`skill-item ${isExpanded ? "expanded" : ""}`} key={skill.command}>
            <button className="skill-toggle" type="button" onClick={() => setExpanded(isExpanded ? "" : skill.command)} aria-expanded={isExpanded}>
              <span><Command size={14} />{skill.label}</span><ChevronRight size={15} />
            </button>
            {isExpanded && <div className="skill-detail"><p>{skill.description}</p><button type="button" onClick={() => onInsert(skill)}>Add <code>/{skill.command}</code></button></div>}
          </article>;
        })}
      </div>
      <p className="skills-footer"><Sparkles size={13} /> Free-form questions are welcome too.</p>
    </aside>
  );
}

export function AskInterface({ siteKey, modelLabel, theme, compact = false, autoFocus = false, onClose, initialPrompt, clearInitialPromptFromUrl = false, history, onHistoryChange }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>(() => history?.messages ?? [initialAssistantMessage]);
  const [input, setInput] = useState("");
  const [mobileSkillsOpen, setMobileSkillsOpen] = useState(false);
  const [evidence, setEvidence] = useState<EvidenceMode>("inferred");
  const [depth, setDepth] = useState<DepthMode>("brief");
  const [busy, setBusy] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [gateError, setGateError] = useState("");
  const [gateReset, setGateReset] = useState(0);
  const [slashIndex, setSlashIndex] = useState(0);
  const [conversationId] = useState(() => history?.conversationId ?? crypto.randomUUID());
  const chatLogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const deliveredDeepLinkPrompt = useRef<string | null>(null);
  const inputId = useId();
  const titleId = useId();

  const shortModelLabel = modelLabel.split(" · ")[0].trim();
  const slashQuery = input.startsWith("/") && !input.slice(1).includes(" ") ? input.slice(1).toLowerCase() : null;
  const slashSkills = useMemo(() => slashQuery === null ? [] : visiblePortfolioSkills.filter((skill) => `${skill.command} ${skill.label}`.toLowerCase().includes(slashQuery)), [slashQuery]);

  useEffect(() => { setSlashIndex(0); }, [slashQuery]);
  useEffect(() => {
    onHistoryChange?.({ conversationId, messages });
  }, [conversationId, messages, onHistoryChange]);
  useEffect(() => {
    const log = chatLogRef.current;
    if (!log) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    log.scrollTo({ top: log.scrollHeight, behavior: reducedMotion ? "auto" : "smooth" });
  }, [messages, busy]);
  useEffect(() => {
    if (!autoFocus) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [autoFocus]);
  useEffect(() => {
    if (!onClose || gateOpen) return;
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [gateOpen, onClose]);

  const insertSkill = useCallback((skill: PortfolioSkill) => {
    setMobileSkillsOpen(false);
    setInput(`/${skill.command} `);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const runPrompt = useCallback(async (prompt: string) => {
    const displayPrompt = prompt.trim();
    const cleanPrompt = resolveSkillPrompt(displayPrompt);
    if (!cleanPrompt || busy) return;
    setBusy(true);
    setInput("");
    const userMessage: ChatMessage = { id: crypto.randomUUID(), role: "user", content: displayPrompt };
    const assistantId = crypto.randomUUID();
    const nextHistory = [...messages, userMessage];
    setMessages([...nextHistory, { id: assistantId, role: "assistant", content: "" }]);

    let content = "";
    let citations: Citation[] = [];
    let inference = false;
    let requestId: string | undefined;
    let feedbackToken: string | undefined;
    let complete = false;
    try {
      await sendChat(cleanPrompt, evidence, depth, nextHistory, conversationId, assistantId, (event) => {
        if (event.type === "meta") { requestId = event.requestId; feedbackToken = event.feedbackToken; }
        if (event.type === "delta") content += event.text;
        if (event.type === "sources") { citations = event.citations; inference = event.inference; }
        if (event.type === "error") content = event.message;
        if (event.type === "done") complete = true;
        setMessages((current) => current.map((message) => message.id === assistantId ? { ...message, content, citations, inference, requestId, feedbackToken, complete } : message));
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401 && error.code === "turnstile_required") {
        setMessages(messages);
        setPendingPrompt(displayPrompt);
        setGateOpen(true);
      } else {
        const message = error instanceof Error ? error.message : "The assistant is temporarily unavailable.";
        setMessages((current) => current.map((item) => item.id === assistantId ? { ...item, content: message, complete: true } : item));
      }
    } finally {
      setBusy(false);
    }
  }, [busy, conversationId, depth, evidence, messages]);

  const onSubmit = (event: FormEvent) => { event.preventDefault(); void runPrompt(input); };
  useEffect(() => {
    if (!initialPrompt || deliveredDeepLinkPrompt.current === initialPrompt) return;
    setInput(initialPrompt);
    const timer = window.setTimeout(() => {
      deliveredDeepLinkPrompt.current = initialPrompt;
      void runPrompt(initialPrompt);
    }, 0);
    if (clearInitialPromptFromUrl) {
      const url = new URL(window.location.href);
      url.searchParams.delete("q");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    }
    return () => window.clearTimeout(timer);
  }, [clearInitialPromptFromUrl, initialPrompt, runPrompt]);
  const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashSkills.length) {
      if (event.key === "ArrowDown") { event.preventDefault(); setSlashIndex((value) => (value + 1) % slashSkills.length); return; }
      if (event.key === "ArrowUp") { event.preventDefault(); setSlashIndex((value) => (value - 1 + slashSkills.length) % slashSkills.length); return; }
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); setInput(""); return; }
      if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); insertSkill(slashSkills[slashIndex]); return; }
    }
    if (event.key === "Escape" && onClose) { event.preventDefault(); event.stopPropagation(); onClose(); return; }
    if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); }
  };

  const completeChallenge = useCallback(async (token: string) => {
    if (!token) return;
    setGateError("");
    try {
      await createAiSession(token);
      const prompt = pendingPrompt;
      setGateOpen(false);
      setPendingPrompt(null);
      if (prompt) window.setTimeout(() => void runPrompt(prompt), 0);
    } catch (error) {
      setGateError(error instanceof Error ? error.message : "The security check failed.");
      setGateReset((value) => value + 1);
    }
  }, [pendingPrompt, runPrompt]);

  const chatInterface = (
    <section id={compact ? "assistant-widget-chat" : undefined} className={`ask-interface ${compact ? "assistant-widget-chat" : ""}`} aria-labelledby={titleId} role={compact ? "dialog" : undefined}>
      <div className="ask-heading">
        <div>
          <span className="section-kicker">{compact ? "Experimental AI bio" : "Interactive bio"}</span>
          <h2 id={titleId}>{compact ? "Ask Omer's AI" : "Ask Omer’s AI Assistant"}</h2>
        </div>
        {compact
          ? <button className="assistant-widget-close" type="button" onClick={onClose} aria-label="Close assistant chat"><X size={18} /></button>
          : <div className="model-badge" title={modelLabel} aria-label={`Model: ${modelLabel}`}><span className="pulse" aria-hidden="true" />{shortModelLabel}</div>}
        {!compact && <p className="ask-capability-note">Experimental AI can make mistakes. Uses approved sources; verify with citations.</p>}
      </div>

      <div className="chat-log" ref={chatLogRef} aria-live="polite" aria-busy={busy}>
        {messages.map((message, index) => (
          <article className={`message ${message.role}`} key={message.id}>
            <div className="avatar" aria-hidden="true">{message.role === "assistant" ? <Bot size={17} /> : <UserRound size={17} />}</div>
            <div className="message-body">
              <div className="message-meta"><strong>{message.role === "assistant" ? "Omer's AI" : "You"}</strong>{message.inference && <span className="inference-tag">Inference</span>}</div>
              <p>{message.content || <ThinkingStatus />}</p>
              {!!message.citations?.length && <div className="citations" aria-label="Sources">{message.citations.map((citation) => citation.url ? <Link key={citation.id} to={citation.url}>{citation.title}</Link> : <span key={citation.id}>{citation.title}</span>)}</div>}
              {message.role === "assistant" && message.id !== "welcome" && message.complete && <ResponseActions message={message} conversation={messages.slice(Math.max(0, index - 20), index + 1)} />}
            </div>
          </article>
        ))}
      </div>

      <div className="composer-wrap">
        {!!slashSkills.length && <div className="slash-menu" role="listbox" aria-label="Available skills">
          <div className="slash-menu-label">Skills</div>
          {slashSkills.map((skill, index) => <button type="button" role="option" aria-selected={slashIndex === index} className={slashIndex === index ? "selected" : ""} key={skill.command} onClick={() => insertSkill(skill)}><span><strong>/{skill.command}</strong><small>{skill.description}</small></span><ChevronRight size={15} /></button>)}
        </div>}
        <form className="composer" onSubmit={onSubmit}>
          <label htmlFor={inputId} className="sr-only">Ask a question about Omer</label>
          <textarea ref={inputRef} id={inputId} value={input} maxLength={600} rows={2} placeholder="Ask a question or type / for skills…" onChange={(event) => setInput(event.target.value)} onKeyDown={onComposerKeyDown} />
          <div className="composer-footer">
            {compact ? <span><kbd>/</kbd> skills · Enter to send</span> : <div className="composer-controls" role="group" aria-label="Assistant response controls">
              <span className="composer-skills-hint"><kbd>/</kbd> skills</span>
              <button className="mobile-skills-toggle" type="button" onClick={() => setMobileSkillsOpen((open) => !open)} aria-expanded={mobileSkillsOpen} aria-controls="mobile-skills-panel"><kbd>/</kbd> skills</button>
              <Selector label="Mode" value={evidence} options={["factual", "inferred", "auto"]} optionLabels={{ factual: "Strict" }} onChange={setEvidence} />
              <Selector label="Response" value={depth} options={["brief", "deep", "auto"]} optionLabels={{ deep: "Detailed" }} onChange={setDepth} />
              <span className="composer-send-hint">Enter to send</span>
            </div>}
            <span className="char-count">{input.length}/600</span>
          </div>
          <button className="send-button" type="submit" disabled={busy || !input.trim()} aria-label="Send question"><ArrowUp size={18} /></button>
        </form>
      </div>
      {compact && <p className="ai-notice assistant-widget-notice"><ShieldCheck size={13} /> Experimental AI can make mistakes. Verify with citations. <Link to="/terms">Terms</Link> · <Link to="/privacy">Privacy</Link></p>}
    </section>
  );

  return (
    <div className={compact ? "assistant-widget-interface" : "bio-home ai-assistant-page assistant-layout"}>
      {!compact && <header className="assistant-page-heading">
        <div>
          <span className="hero-label"><span /> Experimental conversational AI bio</span>
          <h1>Ask Omer's AI Assistant</h1>
          <p>Ask about Omer's experience, leadership style, role fit, and explore the skills.</p>
        </div>
      </header>}

      {compact ? chatInterface : <div className="interaction-grid">
            <aside className={`interaction-side ${mobileSkillsOpen ? "mobile-open" : ""}`} id="mobile-skills-panel">
              <button className="mobile-skills-close" type="button" onClick={() => setMobileSkillsOpen(false)}><X size={15} /> Close skills</button>
              <SkillsPanel onInsert={insertSkill} />
            </aside>
            <div className="interaction-main">
              {SHOW_HOW_IT_WORKS && <section className="how-it-works" aria-labelledby="how-title">
                <div className="section-heading"><span className="section-kicker">How it works</span><h2 id="how-title">Explore in your own way.</h2></div>
                <div className="guide-grid">
                  <article><Command size={18} /><h3>Start with a skill</h3><p>Choose one from the right or type <kbd>/</kbd>. It prepares a prompt but never sends it for you.</p></article>
                  <article><Sparkles size={18} /><h3>Ask anything</h3><p>Explore Omer’s experience, leadership, impact, and fit for a role in your own words.</p></article>
                  <article><FileCheck2 size={18} /><h3>Check the evidence</h3><p>Use response controls and citations. Important decisions should always be independently verified.</p></article>
                </div>
              </section>}

              {chatInterface}
            </div>
          </div>
      }

      {gateOpen && <div className="modal-backdrop" role="presentation">
        <section className="gate-modal" role="dialog" aria-modal="true" aria-labelledby="gate-title">
          <button className="modal-close" type="button" onClick={() => setGateOpen(false)} aria-label="Close"><X size={18} /></button>
          <div className="gate-icon"><LockKeyhole size={22} /></div>
          <span className="section-kicker">One quick security check</span>
          <h2 id="gate-title">Activate the AI session</h2>
          <p>Approved skill answers are always available. A new generated answer requires a brief bot check and uses a privacy-conscious session with strict limits.</p>
          <ul className="gate-list"><li><Check size={14} /> No advertising profile</li><li><Check size={14} /> Raw chats deleted after 30 days</li><li><Check size={14} /> Maximum 10 generated answers per day</li></ul>
            <TurnstileWidget siteKey={siteKey} action="ai-session" theme={theme} onToken={completeChallenge} resetKey={gateReset} />
          {gateError && <p className="form-error"><RotateCcw size={14} /> {gateError}</p>}
          <p className="microcopy">Continuing confirms you are 16+ and agree to the <Link to="/terms">Terms</Link> and acknowledge the <Link to="/privacy">Privacy Policy</Link>.</p>
        </section>
      </div>}
    </div>
  );
}
